import type React from 'react';
import {useState} from 'react';
import {Button, Divider, Paper, Stack, Typography} from '@mui/material';
import {failed} from '../hooks/notices';
import {fieldErrors, useCategorySettingsActions} from '../hooks/useCategorySettings';
import type {SettingsGroup, SettingValue} from '../types/settingsTypes';
import {SettingField} from './SettingField';

type Props = {group: SettingsGroup; children?: React.ReactNode};

const same = (a: SettingValue, b: SettingValue) => JSON.stringify(a) === JSON.stringify(b);

/** One settings group: its fields, edited locally and saved together (with Undo). */
export const SettingsGroupCard: React.FC<Props> = ({group, children}) => {
    const actions = useCategorySettingsActions();
    const [draft, setDraft] = useState<Record<string, SettingValue>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState(false);
    const valueOf = (key: string) => draft[key] ?? group.settings.find(s => s.key === key)!.value;
    const dirty = group.settings.filter(s => s.key in draft &&!same(draft[s.key], s.value)).map(s => s.key);

    const save = () => {
        const changes = Object.fromEntries(dirty.map(k => [k, valueOf(k)]));
        const previous = Object.fromEntries(dirty.map(k => [k, group.settings.find(s => s.key === k)!.value]));
        setBusy(true);
        actions.save(changes, previous)
            .then(() => {
                setDraft({});
                setErrors({});
            })
            .catch(e => {
                const fields = fieldErrors(e);
                if (fields) setErrors(fields);
                else failed(e);
            })
            .finally(() => setBusy(false));
    };

    return (
        <Paper variant="outlined" id={`settings-${group.key}`} sx={{p: 2, scrollMarginTop: 16}}>
            <Stack direction="row" alignItems="flex-start" gap={2}>
                <Stack sx={{flex: 1, minWidth: 0}}>
                    <Typography variant="h6" component="h2">{group.label}</Typography>
                    <Typography variant="body2" color="text.secondary">{group.help}</Typography>
                </Stack>
                {dirty.length > 0 && (
                    <Stack direction="row" gap={1}>
                        <Button size="small" onClick={() => {
                            setDraft({});
                            setErrors({});
                        }} disabled={busy}>Discard</Button>
                        <Button size="small" variant="contained" onClick={save} disabled={busy}>
                            {`Save ${dirty.length} change${dirty.length === 1 ? '' : 's'}`}
                        </Button>
                    </Stack>
                )}
            </Stack>
            {group.settings.length > 0 && (
                <Stack divider={<Divider flexItem/>} sx={{mt: 1}}>
                    {group.settings.map(s => (
                        <SettingField key={s.key} setting={s} value={valueOf(s.key)} error={errors[s.key]}
                                      onChange={v => setDraft({...draft, [s.key]: v})}
                                      onReset={() => setDraft({...draft, [s.key]: s.default})}/>
                    ))}
                </Stack>
            )}
            {children}
        </Paper>
    );
};
